import React from 'react';
import {
    ArrowBackRounded,
    ArrowForwardRounded,
    CloseRounded,
} from '@material-ui/icons';
import styled from 'styled-components/macro';
import ReactPlayer from 'react-player';
import { prop } from 'styled-tools';
import PropTypes from 'prop-types';
import { isMobile } from 'react-device-detect';
import TurnYourDeviceInLandscapeModeOverlay from 'Common/components/TurnYourDeviceInLandscapeModeOverlay';
import useWindowSize from '@react-hook/window-size';
import InteractableOuterBox from 'Common/components/Boxes/InteractableOuterBox';
import colors from 'Common/constants/colors';

const Wrapper = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    z-index: 9999;
    width: 100vw;
    height: 100vh;
    background: ${colors.PAGE_BACKGROUND};
`;

const VideoPlayerWrapper = styled.div`
    position: absolute;
    top: 50%;
    left: 0;
    width: 100%;
    height: 100%;
    transform: translateY(-50%);
`;

const VideoPlayer = styled(ReactPlayer)`
    position: absolute;
    top: 0;
    left: 0;

    video {
        &:focus {
            outline: 0 none;
        }
    }
`;

const SlideArrows = styled.div`
    position: absolute;
    left: 0;
    top: 50%;
    width: 100%;
    height: 70px;
    transform: translateY(-50%);
    pointer-events: none;
`;

const SlideShowImagePreview = styled.div`
  width: 100%;
  height: 100%;
  background-image: url('${prop('src')}');
  background-repeat: no-repeat;
  background-size: contain;
  background-position: 50% 50%;
`;

const CloseButton = styled(InteractableOuterBox).attrs({
    as: 'button',
})`
    display: flex;
    align-items: center;
    justify-content: center;
    position: absolute;
    top: 10px;
    right: 10px;
    width: 40px;
    height: 40px;
    border: 0 none;
    outline: 0 none;
    cursor: pointer;

    svg {
        margin-right: 0;
    }
`;

const ArrowButton = styled.button`
    position: absolute;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 50px;
    height: 50px;
    color: ${colors.PRIMARY};
    background: none;
    border: 0 none;
    cursor: pointer;
    pointer-events: all;
    opacity: 0.75;
    transition: opacity 150ms cubic-bezier(0.4, 0, 0.2, 1);

    &:hover,
    &:focus {
        outline: 0 none;
    }

    &:hover {
        opacity: 1;
    }

    &[disabled] {
        cursor: not-allowed;
        opacity: 0.4;

        &:hover {
            opacity: 0.4;
        }
    }

    svg {
        font-size: 40px;
    }
`;

const PreviousButton = styled(ArrowButton)`
    left: 10px;
`;

const NextButton = styled(ArrowButton)`
    right: 10px;
`;

const SlideShowModal = ({
    slide,
    isFirstSlide,
    isLastSlide,
    onClose,
    onPreviousButtonClick,
    onNextButtonClick,
    ...props
}) => {
    const [width, height] = useWindowSize();

    const isPortrait = width < height;
    const isOverlayVisible = isMobile && isPortrait;

    return (
        <Wrapper {...props}>
            {slide.fileType === 'IMAGE' && (
                <SlideShowImagePreview src={slide.file} />
            )}
            {slide.fileType === 'VIDEO' && (
                <VideoPlayerWrapper>
                    <VideoPlayer
                        url={slide.file}
                        playing={!isOverlayVisible}
                        controls
                        width="100%"
                        height="100%"
                    />
                </VideoPlayerWrapper>
            )}

            <CloseButton onClick={onClose}>
                <CloseRounded />
            </CloseButton>

            <SlideArrows>
                <PreviousButton
                    type="button"
                    onClick={onPreviousButtonClick}
                    disabled={isFirstSlide}
                >
                    <ArrowBackRounded />
                </PreviousButton>
                <NextButton
                    type="button"
                    onClick={onNextButtonClick}
                    disabled={isLastSlide}
                >
                    <ArrowForwardRounded />
                </NextButton>
            </SlideArrows>
            {isOverlayVisible && <TurnYourDeviceInLandscapeModeOverlay />}
        </Wrapper>
    );
};

SlideShowModal.propTypes = {
    slide: PropTypes.object.isRequired,
    isFirstSlide: PropTypes.bool.isRequired,
    isLastSlide: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onPreviousButtonClick: PropTypes.func.isRequired,
    onNextButtonClick: PropTypes.func.isRequired,
};

export default SlideShowModal;
